import { useState, useCallback, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import AuthModal from '../home/components/AuthModal';
import TalentTestQuestion from './components/TalentTestQuestion';
import TalentTestResult from './components/TalentTestResult';
import { questionsEn, questionsZh, TalentType } from './components/TalentTestData';
import { useLang } from '../../i18n/LanguageContext';

type Stage = 'intro' | 'testing' | 'result';

interface CurrentUser {
  name: string;
  email: string;
}

const initialScores: Record<TalentType, number> = { S: 0, J: 0, P: 0, N: 0 };

const RESULT_STORAGE_KEY = (email: string) => `mystic_talent_result_${email}`;

export default function TalentTestPage() {
  const { lang, t } = useLang();
  const questions = lang === 'zh' ? questionsZh : questionsEn;
  const [stage, setStage] = useState<Stage>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scores, setScores] = useState<Record<TalentType, number>>({ ...initialScores });
  const [history, setHistory] = useState<TalentType[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [isFromSave, setIsFromSave] = useState(false);

  // 初始化：读取登录状态 + 历史结果
  useEffect(() => {
    const stored = localStorage.getItem('mystic_current_user');
    if (stored) {
      try {
        const user: CurrentUser = JSON.parse(stored);
        setCurrentUser(user);
        const savedResult = localStorage.getItem(RESULT_STORAGE_KEY(user.email));
        if (savedResult) {
          const parsed = JSON.parse(savedResult);
          setScores(parsed.scores);
          setIsFromSave(true);
          setStage('result');
        }
      } catch { /* ignore */ }
    }
  }, []);

  const startTest = () => {
    setScores({ ...initialScores });
    setHistory([]);
    setCurrentIndex(0);
    setIsFromSave(false);
    setStage('testing');
  };

  const handleStart = () => {
    if (!currentUser) {
      setAuthOpen(true);
      return;
    }
    startTest();
  };

  const handleAuthSuccess = (user: CurrentUser) => {
    setCurrentUser(user);
    setAuthOpen(false);
    // 检查是否有历史结果
    const savedResult = localStorage.getItem(RESULT_STORAGE_KEY(user.email));
    if (savedResult) {
      try {
        const parsed = JSON.parse(savedResult);
        setScores(parsed.scores);
        setIsFromSave(true);
        setStage('result');
      } catch {
        startTest();
      }
    } else {
      startTest();
    }
  };

  const handleAnswer = useCallback((type: TalentType) => {
    const newScores = { ...scores, [type]: scores[type] + 1 };
    setScores(newScores);
    setHistory((prev) => [...prev, type]);

    if (currentIndex + 1 >= questions.length) {
      // 保存结果
      if (currentUser) {
        try {
          localStorage.setItem(
            RESULT_STORAGE_KEY(currentUser.email),
            JSON.stringify({ scores: newScores, timestamp: Date.now() })
          );
        } catch { /* ignore */ }
      }
      setIsFromSave(false);
      setTimeout(() => setStage('result'), 300);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, scores, currentUser]);

  const handleBack = useCallback(() => {
    if (currentIndex === 0) return;
    const prevType = history[history.length - 1];
    setScores((prev) => ({ ...prev, [prevType]: Math.max(0, prev[prevType] - 1) }));
    setHistory((prev) => prev.slice(0, -1));
    setCurrentIndex((prev) => prev - 1);
  }, [currentIndex, history]);

  const handleRetake = () => {
    setStage('intro');
    setCurrentIndex(0);
    setScores({ ...initialScores });
    setHistory([]);
    setIsFromSave(false);
  };

  const TALENT_TYPES = [
    {
      name: t('The Seer', '感知者天赋'),
      en: t('Seer', '感知者'),
      desc: t('Clairvoyance · Perception · Intuition', '千里眼 · 感知 · 直觉'),
      detail: t(
        'A born information receiver — premonitions, vivid dreams, and sixth sense are your innate abilities; the universe\'s signals arrive before you even speak',
        '天生的信息接收者——预感、生动的梦境与第六感是你与生俱来的能力，宇宙的信号在你开口之前已先行抵达'
      ),
      color: '#a78bfa',
      from: '#4c1d95',
      to: '#7c3aed',
      icon: 'ri-eye-2-line',
    },
    {
      name: t('The Interpreter', '解读者天赋'),
      en: t('Interpreter', '解读者'),
      desc: t('Tarot · Astrology · Runes', '塔罗 · 占星 · 如尼文'),
      detail: t(
        'A decoder of cosmic language — symbols, systems, and patterns become profound life insights in your hands; you are born to transform hidden information into guidance',
        '宇宙语言的解码者——符号、系统与规律在你的手中化为深刻的人生洞见，你天生擅长将隐藏的信息转化为指引'
      ),
      color: '#f4d03f',
      from: '#78350f',
      to: '#d97706',
      icon: 'ri-book-open-line',
    },
    {
      name: t('The Practitioner', '实践者天赋'),
      en: t('Practitioner', '实践者'),
      desc: t('Herbs · Ritual · Alchemy', '草药 · 仪式 · 炼金'),
      detail: t(
        'The divine alchemist — you create transformational energy with your hands; touch turns intention into reality; you are the most powerful ritual worker and spiritual craft master',
        '神圣的炼金师——你用双手创造转化能量，触碰将意念变为现实，你是最强大的仪式工作者与灵性手工大师'
      ),
      color: '#34d399',
      from: '#064e3b',
      to: '#059669',
      icon: 'ri-flask-line',
    },
    {
      name: t('The Healer', '疗愈者天赋'),
      en: t('Healer', '疗愈者'),
      desc: t('Energy · Touch · Field', '能量 · 触碰 · 场域'),
      detail: t(
        'A living energy field — your presence itself is healing; you naturally sense and influence energy flow; others unconsciously relax and open in your presence',
        '活着的能量场——你的存在本身就是疗愈，你天然感知并影响能量流动，他人在你身边会不自觉地放松与敞开'
      ),
      color: '#f9a8d4',
      from: '#831843',
      to: '#db2777',
      icon: 'ri-heart-2-line',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0d0221] relative overflow-x-hidden">
      {/* 星空背景 */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {Array.from({ length: 70 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: (i % 3) * 0.8 + 0.6 + 'px',
              height: (i % 3) * 0.8 + 0.6 + 'px',
              top: (i * 13 + 7) % 100 + '%',
              left: (i * 17 + 11) % 100 + '%',
              opacity: 0.05 + (i % 5) * 0.06,
              animation: `twinkle ${(i % 4) + 2.5}s ease-in-out ${(i % 6) * 0.4}s infinite alternate`,
            }}
          />
        ))}
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #a78bfa, transparent)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-6" style={{ background: 'radial-gradient(circle, #f4d03f, transparent)' }} />
        <div className="absolute top-1/2 left-0 w-64 h-64 rounded-full opacity-6" style={{ background: 'radial-gradient(circle, #f9a8d4, transparent)' }} />
      </div>

      <Navbar scrolled />

      {/* ===== 引导页 ===== */}
      {stage === 'intro' && (
        <>
          {/* Hero 全屏 */}
          <section className="relative min-h-screen flex items-center justify-center pt-24 pb-16 px-6">
            <div className="absolute inset-0 z-0">
              <img
                src="https://public.readdy.ai/ai/img_res/41fe174883d1d5bbef02b2947b0f07d1.jpg"
                alt="bg"
                className="w-full h-full object-cover object-top opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0d0221]/60 via-[#0d0221]/40 to-[#0d0221]" />
            </div>

            <div className="relative z-10 w-full max-w-4xl mx-auto text-center space-y-8">
              {/* 徽章 */}
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/8">
                <i className="ri-sparkling-2-line text-[#d4af37] text-sm"></i>
                <span className="text-[#d4af37] text-xs tracking-widest uppercase">
                  {t('Mystic Talent Discovery', '神秘天赋探索')}
                </span>
                <i className="ri-sparkling-2-line text-[#d4af37] text-sm"></i>
              </div>

              <div className="space-y-4">
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-serif leading-tight">
                  <span
                    className="text-transparent bg-clip-text"
                    style={{ backgroundImage: 'linear-gradient(135deg, #d4af37, #f4d03f, #c8b6ff, #f9a8d4)' }}
                  >
                    {t('Discover Your', '发现你的')}
                  </span>
                  <br />
                  <span className="text-white">{t('Spiritual Gift', '灵性天赋')}</span>
                </h1>
                <p className="text-[#c8b6ff] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                  {t(
                    'Everyone carries a divine gift born within them',
                    '每个人都携带着与生俱来的神圣天赋'
                  )}
                  <br />
                  <span className="text-[#d4af37]">
                    {t('12 intuitive questions', '12道直觉问题')}
                  </span>
                  {t(
                    ' to guide you toward your spiritual path',
                    '，引导你找到属于自己的灵性之路'
                  )}
                </p>
              </div>

              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={handleStart}
                  className="group inline-flex items-center gap-3 px-8 sm:px-12 py-4 sm:py-5 rounded-full font-semibold text-base sm:text-lg text-[#1a0a2e] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
                    boxShadow: '0 0 50px rgba(212,175,55,0.35)',
                  }}
                >
                  <i className="ri-sparkling-2-fill text-xl"></i>
                  <span>{t('Begin Gift Test', '开始天赋测试')}</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </button>
                {!currentUser ? (
                  <p className="text-[#c8b6ff]/45 text-sm flex items-center gap-1.5">
                    <i className="ri-lock-line text-xs"></i>
                    <span>{t('Login required to begin · Completely free', '需要登录才能开始 · 完全免费')}</span>
                  </p>
                ) : (
                  <p className="text-[#c8b6ff]/45 text-sm">
                    {t('~3 minutes · Results saved permanently', '约3分钟完成 · 结果永久保存')}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* 天赋类型详细介绍 */}
          <section className="relative z-10 px-4 sm:px-6 pb-20">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <p className="text-xs tracking-[0.3em] uppercase text-[#d4af37]/60 mb-3">
                  {t('4 Paths', '4 条路径')}
                </p>
                <h2 className="text-3xl font-serif text-white">
                  {t('Four Spiritual Gift Paths', '四种灵性天赋路径')}
                </h2>
                <p className="text-[#c8b6ff]/60 text-sm mt-3">
                  {t('The test will help you find the path that fits you best', '测试将帮助你找到最适合自己的那条路')}
                </p>
              </div>

              <div className="space-y-5">
                {TALENT_TYPES.map((item, i) => (
                  <div
                    key={item.name}
                    className={`flex flex-col lg:flex-row ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''} gap-0 rounded-2xl overflow-hidden border`}
                    style={{ borderColor: `${item.color}20` }}
                  >
                    <div
                      className="lg:w-56 flex-shrink-0 flex flex-col items-center justify-center p-8 gap-3"
                      style={{ background: `linear-gradient(145deg, ${item.from}60, ${item.to}40)` }}
                    >
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center"
                        style={{ background: `${item.color}25`, border: `1px solid ${item.color}35` }}
                      >
                        <i className={`${item.icon} text-3xl`} style={{ color: item.color }}></i>
                      </div>
                      <div className="text-center">
                        <p className="text-white font-serif text-lg font-semibold">{item.name}</p>
                        <p className="text-xs mt-1" style={{ color: `${item.color}cc` }}>{item.en}</p>
                      </div>
                      <div
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ background: `${item.color}20`, color: item.color }}
                      >
                        {item.desc}
                      </div>
                    </div>
                    <div className="flex-1 p-7 flex items-center" style={{ background: `${item.from}08` }}>
                      <p className="text-[#c8b6ff]/80 text-sm lg:text-base leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-14">
                <button
                  onClick={handleStart}
                  className="inline-flex items-center gap-2.5 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-semibold text-[#1a0a2e] cursor-pointer whitespace-nowrap hover:scale-105 transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #d4af37, #f4d03f)', boxShadow: '0 0 40px rgba(212,175,55,0.3)' }}
                >
                  <i className="ri-sparkling-fill"></i>
                  <span>{t('Discover My Gift Now', '立即发现我的天赋')}</span>
                </button>
                <p className="text-[#c8b6ff]/40 text-xs mt-4">
                  {t('Test results are for reference — to help you find your best learning path', '测试结果仅供参考——帮助你找到最适合的学习路径')}
                </p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ===== 测试进行中 ===== */}
      {stage === 'testing' && (
        <section className="relative z-10 min-h-screen flex items-start justify-center px-6 pt-28 pb-20">
          <div className="w-full max-w-2xl">
            <TalentTestQuestion
              question={questions[currentIndex]}
              questionIndex={currentIndex}
              totalQuestions={questions.length}
              onAnswer={handleAnswer}
              onBack={handleBack}
            />
          </div>
        </section>
      )}

      {/* ===== 结果页 ===== */}
      {stage === 'result' && (
        <section className="relative z-10 px-6 pt-28 pb-20">
          <div className="max-w-2xl mx-auto">
            <TalentTestResult
              scores={scores}
              onRetake={handleRetake}
              isSaved={isFromSave}
              currentUser={currentUser}
            />
          </div>
        </section>
      )}

      <Footer />

      {/* 登录弹窗 */}
      <AuthModal
        isOpen={authOpen}
        defaultTab="signin"
        onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <style>{`
        @keyframes twinkle {
          from { opacity: 0.04; transform: scale(0.8); }
          to { opacity: 0.45; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
